import { selector, DefaultValue } from "recoil";
import { getRecoil, setRecoil } from "recoil-nexus";
import { decimalString } from "src/constants/types";

import { BetTypeEnum } from "../constants/enums/betType";
import { PriceChangeEnum } from "../constants/enums/priceChange";
import { persistAtom } from "src/common/helpers/store";
import { IEditingBet, IFreeBet } from "src/constants/interfaces/bet";

interface IOpenPopup {
  key: string;
  props?: object;
}

export interface IMainStore {
  betType: BetTypeEnum;
  priceChangeType: PriceChangeEnum;

  systemCombinationCount: number;

  stake: decimalString;

  systemUnitStake?: decimalString;

  lastBetStake: decimalString;

  triggeredBetId: number | null;

  maxStake: number;

  verificationDisabledManually: boolean;

  isSuperbet: boolean;

  showSuccessState: boolean;

  initialized: boolean;

  placeBetLoading: boolean;

  bookBetLoading: boolean;

  editingBet?: IEditingBet | null;

  openPopup?: IOpenPopup;

  isEachWay: boolean;

  isProfitBoost: boolean;

  isFreeBetEnabled: boolean;

  freeBets: IFreeBet[];

  profitBoostPercent?: number | null;
  profitBoostStake?: number | null
}

const mainStore = persistAtom<IMainStore>({
  key: "mainStore",
  omitPersistKeys: ["openPopup", "placeBetLoading", "profitBoostPercent", "profitBoostStake", "isFreeBetEnabled", "freeBets", "stake"],
  default: {
    betType: BetTypeEnum.Single,
    priceChangeType: PriceChangeEnum.AcceptHigherOdds,

    stake: "",

    lastBetStake: "",

    triggerdBetId: null,

    systemCombinationCount: 2,

    verificationDisabledManually: false,

    maxStake: 0,

    isSuperbet: false,

    isEachWay: false,

    isProfitBoost: false,

    isFreeBetEnabled: false,

    freeBets: [],

    showSuccessState: false,

    placeBetLoading: false,

    bookBetLoading: false,

    initialized: false,
  },
  reflector: (val) => {
    if (!val.editingBet) return val;

    return {
      ...val,
      betType: val.editingBet.betType,
      stake: val.editingBet.stake.toString() as decimalString || val.stake,
      systemCombinationCount: val.editingBet.systemCombinationCount || val.systemCombinationCount
    };
  }
});

export const storeSetSuperbet = (isSuperbet: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.isSuperbet !== isSuperbet && setRecoil(mainStore, (mainState) => ({ ...mainState, isSuperbet }));
};

export const storeTriggerdBetId = (triggeredBetId: number | null) => {
  const mainState = getRecoil(mainStore);
  mainState.triggeredBetId !== triggeredBetId &&
    setRecoil(mainStore, (state) => ({ ...state, triggeredBetId }));
};

export const storeSetIsProfitBoost = (isProfitBoost: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.isProfitBoost !== isProfitBoost && setRecoil(mainStore, (mainState) => ({ ...mainState, isProfitBoost }));
};

export const storeSetProfitBoostPercent = (profitBoostPercent?: number | null) => {
  const mainState = getRecoil(mainStore);
  mainState.profitBoostPercent !== profitBoostPercent &&
    setRecoil(mainStore, (mainState) => ({ ...mainState, profitBoostPercent }));
};

export const storeSetEachWay = (isEachWay: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.isEachWay !== isEachWay && setRecoil(mainStore, (mainState) => ({ ...mainState, isEachWay }));
};

export const storeSetVerificationDisabledManually = (verificationDisabledManually: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.verificationDisabledManually !== verificationDisabledManually && setRecoil(mainStore, (mainState) => ({ ...mainState, verificationDisabledManually }));
};

export const storeSetBookBetLoading = (bookBetLoading: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.bookBetLoading !== bookBetLoading &&
    setRecoil(mainStore, (mainState) => ({ ...mainState, bookBetLoading }));
};

export const storeSetShowSuccessState = (showSuccessState: boolean) => {
  const mainState = getRecoil(mainStore);
  mainState.showSuccessState !== showSuccessState &&
    setRecoil(mainStore, (mainState) => ({ ...mainState, showSuccessState }));
};

export const storeSetFreeBets = (freeBets: IFreeBet[]) => {
  setRecoil(mainStore, (mainState) => ({ ...mainState, freeBets }));
};

export const showSuccessStateSelector = selector<boolean>({
  key: "showSuccessStateSelector",
  get: ({ get }) => {
    const { showSuccessState } = get(mainStore);
    return showSuccessState;
  },
  set: ({ set }, newValue) => {
    if (!(newValue instanceof DefaultValue)) {
      set(mainStore, (mainState) => ({ ...mainState, showSuccessState: newValue }));
    }
  },
});

export const popupsSelector = selector<IOpenPopup | undefined>({
  key: "popupsSelector",
  get: ({ get }) => {
    const { openPopup } = get(mainStore);
    return openPopup;
  },
  set: ({ set }, newValue) => {
    if (!(newValue instanceof DefaultValue)) {
      set(mainStore, (mainState) => ({ ...mainState, openPopup: newValue }));
    }
  },
});

export default mainStore;
